import { ContactCompanyType } from 'models/contact_companies'
import { Preferences } from 'models/preferences'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useGetPreferences } from 'services/preferences'
import { getToken } from 'utils/http-client'
import { z } from 'zod'

const PreferencesContext = createContext<{
  preferences: Preferences | undefined
  isLoading: boolean
  isSettingEnabled: (key: string) => boolean
}>({
  preferences: undefined,
  isLoading: true,
  isSettingEnabled: () => false,
})

const COMPANY_TYPE_PREFIX_MAP_KEY = 'company_type_prefix_map'

const DEFAULT_COMPANY_TYPE_PREFIX_MAP: Record<string, string> = {
  [ContactCompanyType.Chain]: 'RE',
  [ContactCompanyType.University]: 'CU',
  [ContactCompanyType.K12District]: 'DI',
  [ContactCompanyType.UserGenerated]: 'UG',
  [ContactCompanyType.Hospital]: 'HO',
  [ContactCompanyType.NursingHome]: 'NH',
}

/** A well-formed prefix map: a plain object whose values are all strings. */
const prefixMapSchema = z.record(z.string(), z.string())

/**
 * Read the persisted company-type prefix map, falling back to the defaults for a
 * missing, empty, or corrupt value. Guards the historical case where
 * `JSON.stringify(undefined)` wrote the literal string `"undefined"` to
 * localStorage, which then made `JSON.parse` throw on every subsequent load.
 */
export function readCompanyTypePrefixMap(): Record<string, string> {
  const stored = localStorage.getItem(COMPANY_TYPE_PREFIX_MAP_KEY)
  if (stored) {
    try {
      const parsed = prefixMapSchema.safeParse(JSON.parse(stored))
      if (parsed.success) return parsed.data
    } catch {
      // Legacy `"undefined"` / corrupt value — fall through to the defaults.
    }
  }
  return DEFAULT_COMPANY_TYPE_PREFIX_MAP
}

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const hasToken = !!getToken()
  const [isLoading, setIsLoading] = useState(true)

  const initialPreferences = useMemo(
    () => ({
      tooltips: {},
      feature_flags: {},
      misc_settings: [],
      company_type_prefix_map: readCompanyTypePrefixMap(),
      reporting_unit: 'lbs',
    }),
    []
  )

  const [localPreferences, setLocalPreferences] =
    useState<Preferences>(initialPreferences)

  const { data: serverPreferences, isLoading: isQueryLoading } =
    useGetPreferences({
      query: {
        enabled: hasToken,
        refetchOnMount: false,
      },
    })

  useEffect(() => {
    if (serverPreferences) {
      setLocalPreferences((prevPreferences) => {
        // Only persist a real map. JSON.stringify(undefined) returns undefined,
        // which setItem coerces to the string "undefined" and breaks the next
        // JSON.parse on load (see readCompanyTypePrefixMap).
        const serverMap = serverPreferences.company_type_prefix_map
        if (serverMap != null) {
          const serverMapJson = JSON.stringify(serverMap)
          if (
            JSON.stringify(prevPreferences.company_type_prefix_map) !==
            serverMapJson
          ) {
            localStorage.setItem(COMPANY_TYPE_PREFIX_MAP_KEY, serverMapJson)
          }
        }
        // The local `Tooltip` model has a vestigial `text` field not on
        // the server; cast across the structural mismatch.
        return serverPreferences as unknown as Preferences
      })
      setIsLoading(false)
    }
  }, [serverPreferences])

  // Keep loading true if we have server data but haven't updated local preferences yet
  const preferencesNotYetSynced =
    !!serverPreferences &&
    localPreferences.reporting_unit !== serverPreferences.reporting_unit
  const actualIsLoading = isQueryLoading || isLoading || preferencesNotYetSynced

  const isSettingEnabled = (key: string): boolean => {
    return (
      localPreferences?.misc_settings?.find((s) => s.key === key)?.value ===
      'true'
    )
  }

  return (
    <PreferencesContext.Provider
      value={{
        preferences: localPreferences,
        isLoading: actualIsLoading,
        isSettingEnabled,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  )
}

export const usePreferences = () => {
  const context = useContext(PreferencesContext)
  return context
}
